import type { DailySignupsData } from "@investhinks/api-client";
import type { Moment } from "moment";
import moment from "moment";
import React, { type CSSProperties, useMemo } from "react";
import Skeleton from "react-loading-skeleton";

import SafeECharts from "../../stats/SafeECharts";

moment.locale("en");

type DayStats = Partial<DailySignupsData["perDay"][string]>;

interface DailySignUpsGraphProps {
  data?: Record<string, DayStats>;
  date: Moment;
  isLoading?: boolean;
  style?: CSSProperties;
  className?: string;
}

const DailySignUpsGraph: React.FC<DailySignUpsGraphProps> = ({
  data = {},
  date,
  isLoading = false,
  style,
  className,
  ...EChartsProps
}) => {
  const { notVerified, nonClients, clients, days } = useMemo(() => {
    if (!data) return { notVerified: [], nonClients: [], clients: [], days: [] };
    return Object.entries(data).reduce(
      (r, [day, stats]) => {
        r.notVerified.push(stats.notVerified || 0);
        r.nonClients.push(stats.nonClients || 0);
        r.clients.push(stats.clients || 0);
        r.days.push(day);
        return r;
      },
      {
        notVerified: new Array<number>(),
        nonClients: new Array<number>(),
        clients: new Array<number>(),
        days: new Array<string>(),
      }
    );
  }, [data]);

  if (isLoading) return <Skeleton height={style?.height || 400} />;

  const grayColor = getComputedStyle(document.documentElement).getPropertyValue("--gray").trim();
  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
  const defaultColor = getComputedStyle(document.documentElement).getPropertyValue("--default").trim();

  return (
    <SafeECharts
      {...EChartsProps}
      className={className}
      style={style}
      option={{
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
            label: {
              formatter: ({ value }: { value: number }) => moment(date).set("date", value).format("DD MMMM YYYY"),
            },
          },
        },
        grid: {
          containLabel: true,
          top: 15,
          right: 10,
          left: 10,
          bottom: 10,
        },
        yAxis: {
          type: "value",
          minInterval: 1,
        },
        xAxis: {
          type: "category",
          data: days,
        },
        series: [
          {
            color: defaultColor,
            name: "Clients",
            type: "bar",
            data: clients,
            stack: "sign-ups",
          },
          {
            color: primaryColor,
            name: "Non clients",
            type: "bar",
            data: nonClients,
            stack: "sign-ups",
          },
          {
            color: grayColor,
            name: "Not verified",
            type: "bar",
            data: notVerified,
            stack: "sign-ups",
          },
        ],
      }}
    />
  );
};

export default DailySignUpsGraph;
